/**
 * settingsWrite — one implementation of "save a setting", shared by the
 * settings sections.
 *
 * Extracted when a second section needed to write settings. The upsert is
 * fiddly enough to be worth having once: find by (merchant, key), update or
 * create, mark unsynced so the row is pushed, and mirror into the store cache
 * so synchronous readers — the print pipeline in particular — see the new value
 * without waiting for an observable to fire.
 *
 * Must be called inside a WatermelonDB writer.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Setting } from '@/db/models/System';
import { useSettingsStore } from '@/store/useSettingsStore';

export interface UpsertSettingArgs {
  merchantId: string;
  key: string;
  value: string;
  /** Grouping label on the row; purely descriptive. */
  category?: string;
}

export async function upsertSetting({
  merchantId,
  key,
  value,
  category = 'invoice',
}: UpsertSettingArgs): Promise<void> {
  const existing = await database
    .get<Setting>('settings')
    .query(Q.where('merchant_id', merchantId), Q.where('setting_key', key))
    .fetch();

  if (existing.length > 0) {
    await existing[0].update((s) => {
      s.settingValue = value;
      s.isSynced = false;
    });
  } else {
    await database.get<Setting>('settings').create((s) => {
      s.merchantId = merchantId;
      s.storeId = null;
      s.category = category;
      s.settingKey = key;
      s.settingValue = value;
      s.version = 1;
      s.isSynced = false;
      s.isDeleted = false;
    });
  }

  // Synchronous readers (the print pipeline reads getState() directly) must see
  // this immediately, not on the next observable tick.
  useSettingsStore.getState().setSettingCache(key, value);
}
